import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/src/components/ui/dialog";
import { Input } from "@/src/components/ui/input";
import { Button } from "@/src/components/ui/button";
import { SheetItem } from "@/src/components/ui/sheet";
import { deleteDatabase } from "@/src/helpers/indexedDB";
import { plumeAPI } from "@/src/helpers/ServiceHelper";
import { captureEvent } from "@/src/helpers/trackEvent";
import { useEditorContext } from "@/src/contexts/editorContext";

const APPROX_CHARS_PER_WORD = 6;

export function DeleteAccountButton() {
  const { getAccessTokenSilently, logout } = useAuth0();
  const { booksMetadata } = useEditorContext();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const stats = {
    totalBooks: booksMetadata.length,
    // Approximate word count from total_characters (raw HTML length).
    totalWords: booksMetadata.reduce((sum, b) => sum + Math.round((b.total_characters || 0) / APPROX_CHARS_PER_WORD), 0),
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== "delete my account") return;

    setIsDeleting(true);
    try {
      const token = await getAccessTokenSilently();

      await Promise.all([
        // Delete user data from backend
        plumeAPI.delete("/users_api/users", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        // Clear local database
        deleteDatabase(),
      ]);

      captureEvent("account_deleted", { total_books: stats.totalBooks, total_words: stats.totalWords });
      // Log out the user
      logout({ returnTo: window.location.origin });
    } catch (error) {
      console.error("Error deleting account:", error);
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div
        role="button"
        onClick={() => {
          captureEvent("account_delete_dialog_opened");
          setIsDeleteDialogOpen(true);
        }}
        className="mx-3 flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 transition-colors"
        style={{
          border: "1px dashed var(--qk-clay)",
          color: "var(--qk-clay)",
          fontFamily: "'Satoshi', system-ui, sans-serif",
          fontSize: 13,
          fontWeight: 500,
        }}
        onMouseOver={(e) => (e.currentTarget.style.background = "color-mix(in srgb, var(--qk-clay) 8%, transparent)")}
        onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <i className="pi pi-trash text-xs"></i>
        <span>Delete Account</span>
      </div>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle style={{ color: "var(--qk-clay)" }}>Delete Account</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3" style={{ color: "var(--qk-ink-2)", fontFamily: "'Satoshi', system-ui, sans-serif", fontSize: 14 }}>
                <p>This action cannot be undone. This will permanently delete:</p>
                <ul className="space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="qk-caps" style={{ color: "var(--qk-clay)" }}>·</span>
                    {stats.totalBooks} book{stats.totalBooks !== 1 ? "s" : ""}
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="qk-caps" style={{ color: "var(--qk-clay)" }}>·</span>
                    {stats.totalWords.toLocaleString()} word{stats.totalWords !== 1 ? "s" : ""}
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="qk-caps" style={{ color: "var(--qk-clay)" }}>·</span>
                    All your account data
                  </li>
                </ul>
                <p className="qk-hand" style={{ fontSize: 15, color: "var(--qk-ink-2)" }}>
                  type &ldquo;delete my account&rdquo; to confirm
                </p>
                <Input
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="delete my account"
                  className="mt-2"
                  style={{
                    background: "transparent",
                    border: "1px solid var(--qk-rule)",
                    color: "var(--qk-ink)",
                    fontFamily: "'Spectral', serif",
                    fontStyle: "italic",
                  }}
                />
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button
              type="button"
              className="qk-pill"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              cancel
            </button>
            <button
              type="button"
              className="qk-pill"
              style={{
                background: "var(--qk-clay)",
                borderColor: "var(--qk-clay)",
                color: "var(--qk-on-clay)",
                opacity: deleteConfirmation !== "delete my account" || isDeleting ? 0.4 : 1,
                cursor: deleteConfirmation !== "delete my account" || isDeleting ? "not-allowed" : "pointer",
              }}
              onClick={handleDeleteAccount}
              disabled={deleteConfirmation !== "delete my account" || isDeleting}
            >
              {isDeleting ? "deleting…" : "delete account"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
